import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { GET_AGE_DISTRIBUTION_CHART_DATA_API } from "../../../../services/ApiCalls";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const StackedChart = () => {
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState(false);

  const fetchAgedistributionGraphData = async () => {
    setLoading(true);
    try {
      const payload = {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };

      const res = await GET_AGE_DISTRIBUTION_CHART_DATA_API(payload);
      if (res?.data?.status === "success") {
        setData(res?.data?.data || {});
      } else {
        console.error("Error:", res?.data?.message || "Failed to fetch");
      }
    } catch (err) {
      console.error("API error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCallApiOnDateChange = () => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        setDateError(true);
        toast.error(t("common.startDateGreaterThanEndDate"));
        return;
      }

      setDateError(false);
      fetchAgedistributionGraphData();
    }
  };
  // 🔥 Fetch again whenever date changes
  // useEffect(() => {
  //   if (startDate && endDate) {

  //     if (new Date(startDate) > new Date(endDate)) {
  //       setDateError(true);
  //       toast.error("Start date cannot be greater than end date");
  //       return; // ❌ do not call API
  //     }

  //     setDateError(false);
  //     fetchAgedistributionGraphData(); // ✅ call only when valid
  //   }
  // }, [startDate, endDate]);
  useEffect(() => {
    fetchAgedistributionGraphData();
  }, []);

  const { series, options } = useMemo(() => {
    const distribution = data?.distribution || [];
    const latest = distribution[distribution.length - 1];

    const ageGroups = latest?.ageGroups
      ? Object.keys(latest.ageGroups)
      : ["18-25", "26-35", "36-45", "46-55", "56+", "unknown"];

    const maleData = [];
    const femaleData = [];
    const unknownData = [];

    ageGroups.forEach((age) => {
      const group = latest?.ageGroups?.[age] || {};
      maleData.push(group.male || 0);
      femaleData.push(group.female || 0);
      unknownData.push(group.unknownGender || 0);
    });

    const totalSum =
      maleData.reduce((a, b) => a + b, 0) +
      femaleData.reduce((a, b) => a + b, 0) +
      unknownData.reduce((a, b) => a + b, 0);

    let finalSeries = [];
    let finalOptions = {};

    if (totalSum > 0) {
      finalSeries = [
        { name: t("dashboard.male"), data: maleData },
        { name: t("dashboard.female"), data: femaleData },
        { name: t("dashboard.unknown"), data: unknownData },
      ];

      finalOptions = {
        chart: {
          type: "bar",
          height: 350,
          stacked: true,
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 6,
            borderRadiusApplication: "end",
          },
        },
        xaxis: {
          categories: ageGroups,
          labels: { style: { fontSize: "12px" } },
        },
        legend: {
          position: "bottom",
          markers: { width: 10, height: 10, radius: 50 },
        },
        colors: ["#4c5eff", "#e8307a", "#a0a0a0"],
        dataLabels: { enabled: false },
        fill: { opacity: 1 },
        grid: { borderColor: "#eee" },
      };
    } else {
      finalSeries = [{ name: t("common.noData"), data: [100] }];
      finalOptions = {
        chart: { type: "bar", stacked: false, toolbar: { show: false } },
        xaxis: { categories: [t("common.noData")] },
        colors: ["#d3d3d3"],
        plotOptions: { bar: { borderRadius: 6 } },
        dataLabels: { enabled: true },
        legend: { show: false },
      };
    }

    return { series: finalSeries, options: finalOptions };
  }, [data, t]);

  return (
    <>
      <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
        <div style={{ width: "150px" }}>
          <label className="small fw-semibold">
            {t("common.startDate")}
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={`form-control ${dateError ? "border-danger" : ""}`}
          />
        </div>

        <div style={{ width: "150px" }}>
          <label className="small fw-semibold">
            {t("common.endDate")}
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={`form-control ${dateError ? "border-danger" : ""}`}
          />
        </div>

        <button
          className="btn btn-primary px-4"
          onClick={handleCallApiOnDateChange}
          disabled={!startDate || !endDate || loading}
        >
          {loading ? t("common.loading") : t("common.apply")}
        </button>
      </div>

      <div className="chartCard">
        <div>
          <h6 className="fw-bold mb-2" style={{ color: "#333" }}>
            {t("dashboard.ageGenderDistribution")}
          </h6>

          <ReactApexChart
            options={options}
            series={series}
            type="bar"
            height={290}
          />
        </div>
      </div>
    </>
  );
};

export default StackedChart;
